/**
 * Pack loading. One code path for every surface:
 *   game (bundled base pack) · player (R2-hosted first-party pack) · editor (in-memory draft)
 *
 * All packs are first-party. There is no user-generated content and no public
 * pack submission; the editor is an internal authoring tool.
 */
import { ENGINE_VERSION, safeParsePack, type PackManifest } from '@hon/schema';

export interface PackSource {
  /** Where pack-relative asset paths resolve against. No trailing slash. */
  baseUrl: string;
  /** Manifest location; defaults to `${baseUrl}/pack.json`. */
  manifestUrl?: string;
}

/**
 * The pack needs a runtime this build does not implement.
 *
 * Distinct from a malformed pack because the remedy is different and the player
 * can act on it: update the app. The host should say so rather than reporting a
 * content error for a pack that is perfectly valid — just newer.
 */
export class EngineTooOldError extends Error {
  readonly kind = 'engine-too-old' as const;
  constructor(readonly required: string, readonly running: string, readonly packId?: string) {
    super(
      `[hon] pack${packId ? ` "${packId}"` : ''} needs engine ${required}, this build is ${running}. Update required.`,
    );
    this.name = 'EngineTooOldError';
  }
}

interface Version { major: number; minor: number; patch: number }

function parseVersion(v: string): Version | undefined {
  const m = /^\^?(\d+)\.(\d+)\.(\d+)$/.exec(v.trim());
  if (!m) return undefined;
  return { major: Number(m[1]), minor: Number(m[2]), patch: Number(m[3]) };
}

const RUNTIME = parseVersion(ENGINE_VERSION)!;

/**
 * Can this runtime play a pack that asks for `required`?
 *
 * Pre-1.0 the MINOR carries breaking changes, so comparing majors alone — as
 * this did while `ENGINE_MAJOR` was `0` — accepts every `0.x` pack ever
 * authored, including ones using opcodes that do not exist here. That is the
 * failure mode the check exists to prevent.
 *
 * The comparison is deliberately looser than npm's caret in the forward
 * direction: `^0.1.0` content keeps playing on an 0.2.0 runtime. Under strict
 * caret semantics every published pack would stop loading the day we bump a
 * minor, which would make dropping old content a side effect of a version bump
 * rather than a decision someone made. We only refuse what we cannot run.
 */
export function isEngineCompatible(required: Version, running: Version = RUNTIME): boolean {
  if (required.major !== running.major) return false;         // different major — genuinely incompatible
  if (required.major === 0) return required.minor <= running.minor;
  return true;                                                 // post-1.0: minors are additive
}

export async function loadPack(src: PackSource, fetchImpl: typeof fetch = fetch): Promise<PackManifest> {
  const url = src.manifestUrl ?? `${src.baseUrl}/pack.json`;
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`[hon] pack fetch failed (${res.status}) at ${url}`);

  const raw: unknown = await res.json();

  /*
   * The version gate runs BEFORE schema validation, not after.
   *
   * A pack built against a newer format fails `safeParsePack` — an unknown
   * opcode is not in the discriminated union — so checking afterwards means the
   * player is told their content is corrupt when it is merely newer than their
   * app. Reading one string off the raw JSON first costs nothing and turns that
   * into an actionable "update required".
   */
  const declared = typeof raw === 'object' && raw !== null && typeof (raw as { engine?: unknown }).engine === 'string'
    ? (raw as { engine: string }).engine
    : '^0.1.0';   // matches the schema default, for packs that omit it
  const packId = typeof raw === 'object' && raw !== null && typeof (raw as { id?: unknown }).id === 'string'
    ? (raw as { id: string }).id
    : undefined;

  const required = parseVersion(declared);
  if (!required) {
    throw new Error(`[hon] pack at ${url} declares an unreadable engine version "${declared}"`);
  }
  if (!isEngineCompatible(required)) {
    throw new EngineTooOldError(declared, ENGINE_VERSION, packId);
  }

  const parsed = safeParsePack(raw);
  if (!parsed.success) {
    const issues = parsed.error.issues.slice(0, 5).map((i) => `  · ${i.path.join('.') || '<root>'}: ${i.message}`);
    throw new Error(`[hon] invalid pack at ${url}\n${issues.join('\n')}`);
  }

  const pack = parsed.data;
  // Cheap structural check — a bad entry would otherwise fail mid-session.
  for (const book of pack.books) {
    for (const ep of book.episodes) {
      if (!ep.scenes.some((s) => s.id === ep.entry)) {
        throw new Error(`[hon] ${pack.id} ${book.id}/${ep.id}: entry scene "${ep.entry}" does not exist`);
      }
    }
  }
  return pack;
}

export function resolveAsset(src: PackSource, path: string): string {
  if (/^(https?:)?\/\//.test(path) || path.startsWith('data:')) return path;
  return `${src.baseUrl}/${path.replace(/^\/+/, '')}`;
}
